import type { FleetUpstreamRow } from '../../lib/database.types'
import { age } from '../../lib/time'
import { WATCHDOG_STALE_MS } from './health'

/**
 * What a department has waiting in Linear that no run has touched yet, as one
 * sentence for the top of its desk.
 *
 * The rest of the desk is arithmetic over `agent_runs`, so it can only report
 * work that has already started. A ticket in Backlog has no run, no event and
 * no row anywhere else in this database -- which is why the fleet publishes
 * this snapshot (`fleet_upstream`, replaced whole) rather than the dashboard
 * growing a Linear client and a server-side secret.
 *
 * **Kept by `state_type`, never by state name.** Linear's category for a state
 * is the only thing that says whether that state means "waiting": `backlog`,
 * `unstarted` and `started` do; `completed`, `canceled` and `duplicate` do not.
 * Matching on names instead would make this line mostly Done -- 41 of the dev
 * department's 59 tickets today -- and it would grow with every ticket closed.
 * A type this app does not recognise is dropped for the same reason it is not
 * rendered: nothing here can tell whether a category nobody has seen means
 * waiting, and guessing either way misstates a figure someone is reading to
 * judge how a department is doing.
 *
 * The names themselves are printed verbatim, as the snapshot stores them. A
 * department chooses its own workflow states, so translating "Spec Draft" into
 * a word this repository prefers would hardcode one department's (product
 * rule 2).
 *
 * **No link out, and that is deliberate.** No table this app reads carries a
 * Linear project or backlog URL -- `fleet_ticket_feeds` has a project *name*,
 * not an address -- so a link could only be built by pasting a workspace path
 * together with a slug or a state name, which is one department's Linear
 * wired into a generic desk. A line with no link beats a link that is right
 * for dev and wrong for every department after it.
 *
 * Freshness is the snapshot's own, on the watchdog's threshold, because the
 * same pass publishes it: a snapshot that has stopped being republished has to
 * read differently from a department with nothing coming.
 *
 * Returns the sentence, or null when there is nothing at all to say -- no rows
 * for the department means the fleet does not count its upstream work, and a
 * desk that never had this line must look exactly as it did.
 */

/**
 * Linear's state categories that mean the work is still waiting, in the order
 * they are read: not picked up, picked up but not begun, begun.
 */
const WAITING_TYPES: ReadonlyArray<string> = ['backlog', 'unstarted', 'started']

/** Where a state type sits in that order, or -1 for one that is not waiting. */
function waitingRank(stateType: string): number {
  return WAITING_TYPES.indexOf(stateType)
}

export function toUpstreamLine(
  rows: ReadonlyArray<FleetUpstreamRow>,
  now: number = Date.now(),
): string | null {
  if (rows.length === 0) return null

  // The snapshot is written whole, so every row carries the same timestamp.
  // The newest is read rather than assumed, so a half-replaced one dates itself
  // by the later write instead of by whichever row came back first.
  let asOf: string | null = null
  let at = NaN
  for (const row of rows) {
    const t = Date.parse(row.generated_at)
    if (!Number.isNaN(t) && (Number.isNaN(at) || t > at)) {
      at = t
      asOf = row.generated_at
    }
  }

  const waiting = rows
    .filter((row) => row.count > 0 && waitingRank(row.state_type) !== -1)
    .sort(
      (a, b) =>
        waitingRank(a.state_type) - waitingRank(b.state_type) ||
        b.count - a.count ||
        a.state.localeCompare(b.state),
    )

  const counts =
    waiting.length === 0
      ? 'Nothing waiting upstream'
      : waiting.map((row) => `${row.count} in ${row.state}`).join(', ')

  // Stale reads differently, rather than showing an old figure as a current one.
  const when =
    Number.isNaN(at) || now - at > WATCHDOG_STALE_MS
      ? `last refreshed ${age(asOf, now)}, so these figures may be out of date`
      : `as of ${age(asOf, now)}`

  return `${counts} — ${when}`
}
